import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BADGES } from "@/lib/badges";

const AuthContext = createContext(null);

async function checkNewBadges(userId) {
  try {
    const { data: progress } = await api.get("/progress");
    const earnedIds = BADGES.filter((b) => b.need(progress)).map((b) => b.id);
    const key = `cq_badges_seen_${userId}`;
    const seenRaw = localStorage.getItem(key);
    if (seenRaw === null) {
      // First time we track badges for this account - seed silently so we don't
      // toast-spam every already-earned badge the moment this feature ships.
      localStorage.setItem(key, JSON.stringify(earnedIds));
      return;
    }
    const seen = new Set(JSON.parse(seenRaw));
    const newly = earnedIds.filter((id) => !seen.has(id));
    if (newly.length > 0) {
      newly.forEach((id) => {
        const badge = BADGES.find((b) => b.id === id);
        if (badge) {
          toast.success(`🏅 Abzeichen freigeschaltet: ${badge.label}`, { description: badge.desc });
        }
      });
      localStorage.setItem(key, JSON.stringify(earnedIds));
    }
  } catch {
    // Badge toast is a nice-to-have - never let it break the auth flow.
  }
}

export function AuthProvider({ children }) {
  // undefined = loading, null = not authed, object = authed
  const [user, setUser] = useState(undefined);

  const loadMe = useCallback(async () => {
    // Tokens used to live in localStorage; drop any leftover so it can't be stolen.
    localStorage.removeItem("cq_token");
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      checkNewBadges(data.id);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Clearing local state is enough if the server is unreachable; the cookie expires.
    }
    setUser(null);
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
